import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * @typedef {object} ProductCardLinkRefs
 * @property {HTMLElement} [cardGallery] - The card gallery element.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 */

/**
 * A custom element for product links with images for transitions to PDP.
 * This is a base class that is extended by ProductCard.
 * Used directly by resource-card.liquid for non-product-card scenarios.
 *
 * @template {ProductCardLinkRefs} [T=ProductCardLinkRefs]
 * @extends {Component<T>}
 */
export class ProductCardLink extends Component {
  get productTransitionEnabled() {
    return this.getAttribute('data-product-transition') === 'true';
  }

  get featuredMediaUrl() {
    return this.getAttribute('data-featured-media-url');
  }

  /**
   * Handles the click event for view transitions.
   * @param {Event} event
   */
  handleViewTransition(event) {
    // If the event has been prevented, don't do anything, another component is handling the click
    if (event.defaultPrevented) return;

    // If the event was on an interactive element, don't do anything, this is not a navigation
    if (event.target instanceof Element) {
      const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');
      if (interactiveElement) return;
    }

    if (!this.productTransitionEnabled) return;

    const { cardGallery } = this.refs;
    if (!cardGallery || !cardGallery.hasAttribute('data-view-transition-to-main-product')) return;

    // Check on the current active image, whether it's a product card image or a resource card image
    const { imagesToTransition } = this.refs;
    const activeImage =
      imagesToTransition?.find(
        (/** @type {HTMLImageElement} */ image) =>
          image.closest('slideshow-slide')?.getAttribute('aria-hidden') === 'false'
      ) ||
      imagesToTransition?.find((/** @type {HTMLImageElement} */ image) =>
        Boolean(image.closest('.card-gallery__media'))
      ) ||
      imagesToTransition?.[imagesToTransition.length - 1];

    if (activeImage instanceof HTMLImageElement) this.#setImageSrcset(activeImage);

    cardGallery.setAttribute('data-view-transition-type', 'product-image-transition');
    cardGallery.setAttribute('data-view-transition-triggered', 'true');
  }

  /**
   * Sets the srcset for the image
   * @param {HTMLImageElement} image
   */
  #setImageSrcset(image) {
    if (!this.featuredMediaUrl) return;

    const currentImageUrl = new URL(image.currentSrc);

    // Deliberately not using origin, as it includes the protocol, which is usually skipped for featured media
    const currentImageRawUrl = currentImageUrl.host + currentImageUrl.pathname;

    if (!this.featuredMediaUrl.includes(currentImageRawUrl)) {
      const imageFade = image.animate([{ opacity: 0.8 }, { opacity: 1 }], {
        duration: 125,
        easing: 'ease-in-out',
      });

      imageFade.onfinish = () => {
        image.srcset = this.featuredMediaUrl ?? '';
      };
    }
  }
}

if (!customElements.get('product-card-link')) {
  customElements.define('product-card-link', ProductCardLink);
}

/**
 * A custom element that displays a product card.
 * Extends ProductCardLink to inherit view transition functionality.
 *
 * @typedef {object} ProductCardRefs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 * @extends {ProductCardLink<ProductCardRefs>}
 */
export class ProductCard extends ProductCardLink {
  requiredRefs = ['productCardLink'];

  /** @type {HTMLElement | null} */
  #hoverTarget = null;

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) {
      console.warn('[product-card] Missing product card link', this);
      return;
    }
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);

    this.#bindShadeSwatchListeners();
    this.#bindShadeGalleryHover();
    this.addEventListener('theme:product-card-refresh', this.#handleProductCardRefresh);
    window.addEventListener('pageshow', this.#handlePageShow);

    requestAnimationFrame(() => {
      this.#ensureShadeSwatchSelected();
      this.#updateShadeLabel();
      this.#updateBadges();
      this.#updateAddToBagVariant();
      this.#updateProductLinkFromSwatch();
      // Do not sync gallery images on first paint — Liquid already rendered the
      // correct primary/hover pair. Syncing here was stripping hover.
      this.#bindSwatchSliderArrows();
      this.#preloadHoverImage();
    });
  }

  /**
   * On history navigation the browser restores the checked swatch, but the markup still
   * carries the server-rendered shade. Re-sync so the image matches the restored swatch.
   */
  #handlePageShow = () => {
    requestAnimationFrame(() => {
      this.#updateShadeLabel();
      this.#updateBadges();
      this.#updateAddToBagVariant();
      this.#updateProductLinkFromSwatch();
      this.#syncGalleryToCheckedSwatch();
    });
  };

  /**
   * Applies the checked swatch's media only when it differs from what is displayed, so an
   * ordinary load keeps the richer primary/hover pair that Liquid resolved.
   */
  #syncGalleryToCheckedSwatch() {
    const selectedInput = this.querySelector('swatches-variant-picker-component input[type="radio"]:checked');
    if (!(selectedInput instanceof HTMLInputElement)) return;

    const mediaUrl = selectedInput.dataset.optionMediaUrl;
    if (!mediaUrl) return;

    const selectedMediaId = selectedInput.dataset.optionMediaId || '';
    const cardMedia = this.refs.cardMedia ?? this.querySelector('[ref="cardMedia"]');
    const currentMediaId = cardMedia instanceof HTMLElement ? cardMedia.dataset.mediaId || '' : '';

    if (selectedMediaId && currentMediaId) {
      if (selectedMediaId === currentMediaId) return;
    } else if (this.#getCardMediaWrap()?.dataset.primarySrc === mediaUrl) {
      return;
    }

    this.#updateCardImageFromSwatch(selectedInput);
  }

  /**
   * Re-initializes shade gallery state after the card DOM is replaced (e.g. collection tab morph).
   */
  #handleProductCardRefresh = () => {
    this.updatedCallback();
    this.dataset.hoverBound = 'false';

    requestAnimationFrame(() => {
      this.#ensureShadeSwatchSelected();
      this.#updateShadeLabel();
      this.#updateBadges();
      this.#updateAddToBagVariant();
      this.#updateProductLinkFromSwatch();
      this.#updateShadeGallery();
      this.#bindSwatchSliderArrows();
      this.#bindShadeGalleryHover();
      this.#preloadHoverImage();
    });
  };

  /**
   * Binds direct listeners for shade swatch changes on product cards.
   */
  #bindShadeSwatchListeners() {
    const swatchPicker = this.querySelector('swatches-variant-picker-component');
    const swatchForm = swatchPicker?.querySelector('form');

    swatchForm?.addEventListener('change', (event) => {
      if (!(event.target instanceof HTMLInputElement)) return;
      if (!event.target.name?.includes('-swatch')) return;

      this.#updateShadeLabel();
      this.#updateBadges();
      this.#updateAddToBagVariant();
      this.#updateProductLinkFromSwatch();
      this.#scheduleShadeGalleryUpdate();
    });
  }

  /**
   * Schedules a shade gallery refresh on the next animation frame.
   */
  #scheduleShadeGalleryUpdate() {
    requestAnimationFrame(() => {
      this.#updateShadeGallery();
    });
  }

  /**
   * Binds hover preview for second image on the gallery/image only
   * (not the whole card — title, swatches, price should not swap the image).
   */
  #bindShadeGalleryHover() {
    this.#removeHoverLayerOnMobile();

    const previousTarget = this.#hoverTarget;
    if (previousTarget instanceof HTMLElement) {
      previousTarget.removeEventListener('pointerenter', this.#onCardHoverEnter);
      previousTarget.removeEventListener('pointerleave', this.#onCardHoverLeave);
    }

    this.removeEventListener('pointerenter', this.#onCardHoverEnter);
    this.removeEventListener('pointerleave', this.#onCardHoverLeave);

    const gallery =
      this.querySelector('.card-gallery') ||
      this.#getCardMediaWrap();

    if (!(gallery instanceof HTMLElement)) {
      this.#hoverTarget = null;
      return;
    }

    this.#hoverTarget = gallery;
    gallery.addEventListener('pointerenter', this.#onCardHoverEnter);
    gallery.addEventListener('pointerleave', this.#onCardHoverLeave);
    this.#ensureHoverLayerFromDataset();
    this.#preloadHoverImage();
  }

  /**
   * Drops the server-rendered hover layer entirely below the mobile breakpoint.
   *
   * The same HTML is served to every viewport, so Liquid has no way to omit it. The CSS in
   * card-gallery.liquid already keeps it out of layout, which is what stops its lazy <img>
   * being fetched before this runs; removing the node as well leaves the card's DOM with
   * only what the layout can actually use. `data-hover-src` stays on the wrapper, so a
   * resize up to desktop can still rebuild the layer from it.
   */
  #removeHoverLayerOnMobile() {
    if (isDesktopBreakpoint()) return;

    const wrap = this.#getCardMediaWrap();
    const layer =
      wrap?.querySelector('.card-gallery__image--hover') ?? this.querySelector('.card-gallery__image--hover');

    layer?.remove();
  }

  #onCardHoverEnter = (event) => {
    if (event.pointerType && event.pointerType !== 'mouse') return;
    this.#showHoverImage();
  };

  #onCardHoverLeave = (event) => {
    if (event.pointerType && event.pointerType !== 'mouse') return;
    this.#showPrimaryImage();
  };

  /**
   * @returns {HTMLElement | null}
   */
  #getCardMediaWrap() {
    const wrap =
      this.refs.cardMediaWrap ??
      this.querySelector('[ref="cardMediaWrap"]') ??
      this.querySelector('.card-gallery__media') ??
      this.querySelector('.card-gallery .product-media-container') ??
      this.querySelector('.card-gallery');
    return wrap instanceof HTMLElement ? wrap : null;
  }

  /**
   * @returns {HTMLImageElement | null}
   */
  #getCardImage() {
    const wrap = this.#getCardMediaWrap();
    const image =
      wrap?.querySelector('.card-gallery__image--primary img.product-media__image') ??
      wrap?.querySelector(':scope > .card-gallery__image--primary img') ??
      wrap?.querySelector('img.product-media__image') ??
      this.querySelector('.card-gallery__image--primary img.product-media__image') ??
      this.querySelector('.card-gallery img.product-media__image');
    return image instanceof HTMLImageElement ? image : null;
  }

  /**
   * @returns {HTMLImageElement | null}
   */
  #getCardHoverImage() {
    const wrap = this.#getCardMediaWrap();
    const image =
      wrap?.querySelector('.card-gallery__image--hover img.product-media__image') ??
      wrap?.querySelector('.card-gallery__image--hover img') ??
      this.querySelector('.card-gallery__image--hover img.product-media__image');
    return image instanceof HTMLImageElement ? image : null;
  }

  /**
   * Creates a hover image layer from data-hover-src when Liquid did not render one.
   */
  #ensureHoverLayerFromDataset() {
    if (!isDesktopBreakpoint()) return null;

    const wrap = this.#getCardMediaWrap();
    if (!(wrap instanceof HTMLElement)) return null;

    const hoverSrc = wrap.dataset.hoverSrc || '';
    if (!hoverSrc || hoverSrc === wrap.dataset.primarySrc) {
      return this.#getCardHoverImage();
    }

    wrap.classList.add('card-gallery__media--has-hover');
    wrap.setAttribute('data-has-hover-image', '');

    let hoverImage = this.#getCardHoverImage();
    if (hoverImage) return hoverImage;

    return this.#createHoverMediaLayer(wrap, hoverSrc, wrap.dataset.hoverSrcset || '');
  }

  #showHoverImage() {
    const wrap = this.#getCardMediaWrap();
    if (!(wrap instanceof HTMLElement)) return;

    const hoverSrc = wrap.dataset.hoverSrc || '';
    if (!hoverSrc || !wrap.hasAttribute('data-has-hover-image')) return;
    if (hoverSrc === wrap.dataset.primarySrc) return;

    const hoverImage = this.#ensureHoverLayerFromDataset();
    if (hoverImage instanceof HTMLImageElement) {
      if (hoverImage.getAttribute('src') !== hoverSrc) {
        hoverImage.src = hoverSrc;
        const hoverSrcset = wrap.dataset.hoverSrcset || '';
        if (hoverSrcset) {
          hoverImage.srcset = hoverSrcset;
        } else {
          hoverImage.removeAttribute('srcset');
        }
      }
      hoverImage.removeAttribute('loading');
    }

    this.classList.add('is-card-hovering');
    wrap.classList.add('is-showing-hover');
  }

  #showPrimaryImage() {
    const wrap = this.#getCardMediaWrap();
    this.classList.remove('is-card-hovering');
    wrap?.classList.remove('is-showing-hover');
  }

  #preloadHoverImage() {
    if (!isDesktopBreakpoint()) return;

    const wrap = this.#getCardMediaWrap();
    const hoverSrc = wrap?.dataset.hoverSrc;
    if (hoverSrc) {
      const preload = new Image();
      preload.src = hoverSrc;
    }
    const hoverImage = this.#getCardHoverImage();
    if (hoverImage?.dataset.src) {
      hoverImage.src = hoverImage.dataset.src;
    }
  }

  // Gallery-level declarative handlers kept for theme compatibility.
  previewImage(_event) {}

  resetImage(_event) {}

  #selectVisibleShadeSlide() {}

  #getVisibleShadeSlides() {
    return [];
  }

  #bindShadeGalleryNavigation() {}

  #handleShadeGalleryControlClick = () => {};

  #onShadeGalleryPointerEnter = () => {};

  #onShadeGalleryPointerLeave = () => {};

  /**
   * Ensures a swatch is selected when the card uses shade-based galleries.
   */
  #ensureShadeSwatchSelected() {
    const hasShadeGallery = this.querySelector('[data-shade-gallery="true"]');
    if (!hasShadeGallery) return;

    const checkedInput = this.querySelector('swatches-variant-picker-component input[type="radio"]:checked');
    if (checkedInput instanceof HTMLInputElement) return;

    const firstInput = this.querySelector('swatches-variant-picker-component input[type="radio"]');
    if (firstInput instanceof HTMLInputElement) {
      firstInput.checked = true;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('theme:product-card-refresh', this.#handleProductCardRefresh);
    this.removeEventListener('click', this.navigateToProduct);
    window.removeEventListener('pageshow', this.#handlePageShow);
    this.removeEventListener('pointerenter', this.#onCardHoverEnter);
    this.removeEventListener('pointerleave', this.#onCardHoverLeave);
    if (this.#hoverTarget instanceof HTMLElement) {
      this.#hoverTarget.removeEventListener('pointerenter', this.#onCardHoverEnter);
      this.#hoverTarget.removeEventListener('pointerleave', this.#onCardHoverLeave);
    }
    this.#hoverTarget = null;
    this.classList.remove('is-card-hovering');
    this.dataset.hoverBound = 'false';
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }

    this.#updateShadeLabel();
    this.#updateAddToBagVariant();
    this.#updateProductLinkFromSwatch();
    this.#scheduleShadeGalleryUpdate();
  };

  /**
   * Syncs the add-to-bag form with the currently selected swatch variant.
   */
  #updateAddToBagVariant() {
    const selectedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('swatches-variant-picker-component input[type="radio"]:checked')
    );
    const variantInputs = this.querySelectorAll('.product-card-add-to-bag input[name="id"]');
    const addToBag = this.querySelector('.product-card-add-to-bag');

    const isNoSell =
      this.dataset.productNoSell === 'true' ||
      selectedInput?.dataset.noSell === 'true' ||
      addToBag?.dataset.productNoSell === 'true';
    this.classList.toggle('product-card--no-sell', Boolean(isNoSell));
    this.dataset.noSell = isNoSell ? 'true' : 'false';

    if (addToBag instanceof HTMLElement) {
      addToBag.dataset.noSell = isNoSell ? 'true' : 'false';
      addToBag.classList.toggle('product-card-add-to-bag--no-sell', Boolean(isNoSell));
      addToBag.hidden = Boolean(isNoSell);
    }

    if (!variantInputs.length) return;

    const variantId = selectedInput?.dataset.variantId || '';
    const isAvailable = selectedInput?.dataset.optionAvailable === 'true';

    variantInputs.forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;

      if (variantId) {
        input.value = variantId;
      }

      input.disabled = !isAvailable || !variantId || isNoSell;
    });

    this.#toggleAddToCartButton(isAvailable && Boolean(variantId) && !isNoSell);
    if (selectedInput) this.#updateAddToBagLabel(selectedInput.dataset.backorderBlocked === 'true');
  }

  /**
   * Shows "Sold out" on the card button for shades blocked by custom.allow_backorder = false
   * (same as the PDP). Collection-page buttons are handled by collection-notify-button.js.
   * @param {boolean} isBackorderBlocked
   */
  #updateAddToBagLabel(isBackorderBlocked) {
    this.querySelectorAll('.product-card-add-to-bag .add-to-cart__button:not([data-collection-add-to-cart])').forEach(
      (button) => {
        if (!(button instanceof HTMLElement)) return;
        const label = button.querySelector('[data-add-to-cart-label]');
        const text = isBackorderBlocked ? button.dataset.soldOutText : button.dataset.defaultText;
        if (label && text) label.textContent = text;
      }
    );
  }

  /**
   * Points the card links at the checked swatch's variant. The section fetch also does this, but only
   * once it resolves, so a click made right after picking a shade would otherwise open the old variant.
   */
  #updateProductLinkFromSwatch() {
    const selectedInput = this.querySelector('swatches-variant-picker-component input[type="radio"]:checked');
    if (!(selectedInput instanceof HTMLInputElement)) return;

    const variantId = selectedInput.dataset.variantId;
    if (!variantId) return;

    const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;
    if (!(productCardLink instanceof HTMLAnchorElement) || !productCardLink.getAttribute('href')?.trim()) return;

    // Combined listings render each shade as a separate product, so follow the swatch's own product.
    const connectedProductUrl = selectedInput.dataset.connectedProductUrl;
    const url = new URL(connectedProductUrl || productCardLink.href, window.location.origin);
    url.searchParams.set('variant', variantId);

    const href = url.toString();
    if (href === productCardLink.href) return;

    for (const link of [productCardLink, cardGalleryLink, productTitleLink]) {
      if (link instanceof HTMLAnchorElement) link.href = href;
    }
  }

  /**
   * Updates the shade label when a swatch is selected.
   * Uses shade_name metafields from data-shade-name-en/ar (Liquid-rendered).
   */
  #updateShadeLabel() {
    const selectedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('swatches-variant-picker-component input[type="radio"]:checked')
    );
    const shadeValues = this.querySelectorAll('[data-shade-value]');

    if (!selectedInput || !shadeValues.length) return;

    const html = document.documentElement;
    const lang = (html.getAttribute('lang') || '').toLowerCase();
    const path = (window.location.pathname || '').toLowerCase();
    const isArabic =
      html.classList.contains('is-arabic') ||
      lang === 'ar' ||
      lang.startsWith('ar-') ||
      path.includes('/ar/') ||
      path.includes('/ar-');

    const shadeEn = selectedInput.getAttribute('data-shade-name-en') || '';
    const shadeAr = selectedInput.getAttribute('data-shade-name-ar') || '';
    const exactLabel = (isArabic ? shadeAr || shadeEn : shadeEn || shadeAr) || '';

    shadeValues.forEach((element) => {
      const en = element.querySelector('[data-pdp-mf-lang="en"]');
      const ar = element.querySelector('[data-pdp-mf-lang="ar"]');
      if (en || ar) {
        if (en) en.textContent = shadeEn;
        if (ar) ar.textContent = shadeAr;
        if (isArabic) {
          if (ar && shadeAr) {
            ar.hidden = false;
            if (en) en.hidden = true;
          } else if (en && shadeEn) {
            en.hidden = false;
            if (ar) ar.hidden = true;
          }
        } else {
          if (en) en.hidden = !shadeEn;
          if (ar) ar.hidden = true;
        }
        return;
      }

      if (element.querySelector('pdp-metafield-content')) return;
      if (exactLabel) element.textContent = exactLabel;
    });
  }

  /**
   * Shows the NEW / BESTSELLER labels that apply to the checked swatch's variant and hides the
   * rest. Liquid renders every badge any variant carries, each tagged with the variant ids it
   * applies to (`data-variants=",id,id,"`), because a swatch change does not re-render the labels.
   */
  #updateBadges() {
    const labels = this.querySelectorAll('.product-tag-labels__label[data-variants]');
    if (!labels.length) return;

    const variantId = this.getSelectedVariantId();
    if (!variantId) return;

    labels.forEach((label) => {
      if (label instanceof HTMLElement) {
        label.hidden = !(label.dataset.variants ?? '').includes(`,${variantId},`);
      }
    });
  }

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      const selectedInput = this.querySelector(
        'swatches-variant-picker-component input[type="radio"]:checked'
      );
      const selectedOptionValueId =
        selectedInput instanceof HTMLInputElement ? selectedInput.dataset.optionValueId : undefined;

      this.variantPicker?.updateVariantPicker(event.detail.data.html);

      if (selectedOptionValueId) {
        const restoredInput = this.querySelector(
          `swatches-variant-picker-component input[type="radio"][data-option-value-id="${selectedOptionValueId}"]`
        );
        if (restoredInput instanceof HTMLInputElement) {
          restoredInput.checked = true;
        }
      }
    }

    this.#scheduleShadeGalleryUpdate();
    this.#updateShadeLabel();
    this.#updateBadges();
    this.#updateAddToBagVariant();
    this.#bindSwatchSliderArrows();

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const responseProductCard = event.detail.data.html?.querySelector('product-card');
    const anchorElement = responseProductCard?.querySelector('a');
    const featuredMediaUrl = responseProductCard?.getAttribute('data-featured-media-url');

    // Update the featured media URL for view transitions (inherited from ProductCardLink)
    if (featuredMediaUrl) {
      this.setAttribute('data-featured-media-url', featuredMediaUrl);
    }

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const buttons = this.querySelectorAll(
      '.product-card-add-to-bag button[type="submit"], .product-card-add-to-bag .add-to-cart__button'
    );

    buttons.forEach((button) => {
      if (button instanceof HTMLButtonElement) {
        button.disabled = !enable;
      }
    });

    this.querySelectorAll('.product-card-add-to-bag add-to-cart-component').forEach((component) => {
      const button = component.querySelector('button[type="submit"]');
      if (button instanceof HTMLButtonElement) {
        button.disabled = !enable;
      }
    });
  }

  /**
   * @param {HTMLInputElement} input
   * @returns {string[]}
   */
  #getSelectedShadeKeys(input) {
    /** @type {Set<string>} */
    const keys = new Set();

    /** @param {string | undefined} key */
    const addKey = (key) => {
      if (!key) return;
      keys.add(key.toLowerCase());
    };

    addKey(input.dataset.shadeKey);

    if (input.value) {
      const normalized = input.value.toLowerCase().trim();
      addKey(normalized.replace(/\s+/g, '_'));
      addKey(normalized.replace(/\s+/g, '-'));

      const beforeDash = normalized.split('-')[0]?.trim();
      if (beforeDash) {
        addKey(beforeDash.replace(/\s+/g, '_'));
        const beforeDashParts = beforeDash.split(/\s+/).filter(Boolean);
        if (beforeDashParts.length) {
          addKey(beforeDashParts[0].replace(/\s+/g, '_'));
          addKey(beforeDashParts[beforeDashParts.length - 1].replace(/\s+/g, '_'));
        }
      }

      const parts = normalized.split(/\s+/).filter(Boolean);
      if (parts.length) {
        addKey(parts[parts.length - 1].replace(/\s+/g, '_'));
      }
    }

    return [...keys];
  }

  /**
   * @param {HTMLElement} slide
   * @returns {string}
   */
  #getSlideSearchText(slide) {
    const img = slide.querySelector('img');
    const srcset = img?.getAttribute('srcset') ?? '';
    const parts = [
      slide.getAttribute('data-shade-search'),
      img?.getAttribute('src'),
      img?.getAttribute('alt'),
      img?.currentSrc,
      srcset,
    ];

    return parts.filter(Boolean).join(' ').toLowerCase();
  }

  /**
   * @param {HTMLElement} slide
   * @returns {boolean}
   */
  #isSwatchMediaSlide(slide) {
    return this.#getSlideSearchText(slide).includes('swatch');
  }

  /**
   * @param {string[]} shadeKeys
   * @param {string} searchText
   * @returns {boolean}
   */
  #shadeKeysMatchFilename(shadeKeys, searchText) {
    if (!searchText || !shadeKeys.length) return false;

    return shadeKeys.some((key) => key && searchText.includes(key.toLowerCase()));
  }

  /**
   * @param {string[]} shadeKeys
   * @param {string} searchText
   * @returns {boolean}
   */
  #shadeKeysMatchSearchText(shadeKeys, searchText) {
    if (!searchText) return false;

    if (this.#shadeKeysMatchFilename(shadeKeys, searchText)) {
      return true;
    }

    return shadeKeys.some((key) => {
      const normalizedKey = key.toLowerCase();
      if (!normalizedKey) return false;

      const pattern = new RegExp(`_other_${normalizedKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?:_|\\d|\\.|$)`);
      return pattern.test(searchText);
    });
  }

  /**
   * @param {string[]} shadeKeys
   * @param {string} searchText
   * @returns {number}
   */
  #getShadeOrderFromSearchText(shadeKeys, searchText) {
    for (const key of shadeKeys) {
      const normalizedKey = key.toLowerCase();
      if (!normalizedKey) continue;

      const needle = `_other_${normalizedKey}`;
      if (!searchText.includes(needle)) continue;

      const remainder = searchText.split(needle)[1]?.split(/[.? \t]/)[0] ?? '';
      if (!remainder) return 0;

      if (remainder.startsWith('_')) {
        const suffix = remainder.slice(1);
        const suffixNumber = Number.parseInt(suffix, 10);
        if (!Number.isNaN(suffixNumber) && String(suffixNumber) === suffix) {
          return suffixNumber + 1;
        }
        return 50;
      }

      const suffixNumber = Number.parseInt(remainder, 10);
      if (!Number.isNaN(suffixNumber) && String(suffixNumber) === remainder) {
        return suffixNumber + 1;
      }
    }

    return 100;
  }

  /**
   * @param {string | null | undefined} raw
   * @returns {Record<string, string[]>}
   */
  #parseShadeMediaMap(raw) {
    /** @type {Record<string, string[]>} */
    const map = {};
    if (!raw) return map;

    for (const entry of raw.split('|').filter(Boolean)) {
      const colonIndex = entry.indexOf(':');
      if (colonIndex === -1) continue;

      const key = entry.slice(0, colonIndex).toLowerCase();
      const ids = entry
        .slice(colonIndex + 1)
        .split(',')
        .filter(Boolean)
        .map(String);

      if (key && ids.length) {
        map[key] = ids;
      }
    }

    return map;
  }

  /**
   * @returns {Record<string, string[]>}
   */
  #getShadeMediaMap() {
    const slideshow = this.refs.slideshow;
    if (!slideshow) return {};

    return this.#parseShadeMediaMap(slideshow.getAttribute('data-shade-media-map'));
  }

  /**
   * @param {string[]} shadeKeys
   * @returns {Set<string>}
   */
  #getMediaIdsForShadeKeys(shadeKeys) {
    const map = this.#getShadeMediaMap();
    /** @type {Set<string>} */
    const ids = new Set();

    for (const key of shadeKeys) {
      const normalizedKey = key.toLowerCase();
      const matchedIds = map[normalizedKey] || [];
      matchedIds.forEach((id) => ids.add(String(id)));
    }

    return ids;
  }

  /**
   * @param {HTMLElement} slide
   * @param {string[]} shadeKeys
   * @returns {boolean}
   */
  #slideHasShadeTag(slide, shadeKeys) {
    const tags = slide.getAttribute('data-shade-tags')?.split(',').filter(Boolean) ?? [];
    return shadeKeys.some((key) => tags.includes(key.toLowerCase()));
  }

  /**
   * @param {HTMLElement} slide
   * @param {string[]} shadeKeys
   * @param {Set<string>} mediaIds
   */
  #slideMatchesSelectedShade(slide, shadeKeys, mediaIds) {
    if (this.#isSwatchMediaSlide(slide)) {
      return false;
    }

    const slideId = slide.getAttribute('slide-id');
    if (slideId && mediaIds.size > 0 && mediaIds.has(String(slideId))) {
      return true;
    }

    if (this.#slideHasShadeTag(slide, shadeKeys)) {
      return true;
    }

    return this.#shadeKeysMatchSearchText(shadeKeys, this.#getSlideSearchText(slide));
  }

  /**
   * @param {HTMLElement[]} slides
   * @param {HTMLInputElement} selectedInput
   * @returns {HTMLElement[]}
   */
  #getFallbackShadeSlides(slides, selectedInput) {
    /** @type {HTMLElement[]} */
    const fallbacks = [];
    const visibleSlides = slides.filter((slide) => !this.#isSwatchMediaSlide(slide));
    const selectedOptionValueId = selectedInput.dataset.optionValueId;
    const featuredMediaId = selectedInput.dataset.optionMediaId;

    if (selectedOptionValueId) {
      for (const slide of visibleSlides) {
        const optionValueIds = slide.getAttribute('data-option-value-ids')?.split(',').filter(Boolean) ?? [];
        if (optionValueIds.some((id) => String(id) === String(selectedOptionValueId))) {
          fallbacks.push(slide);
        }
      }
    }

    if (!fallbacks.length && featuredMediaId) {
      const featuredSlide = visibleSlides.find((slide) => slide.getAttribute('slide-id') === String(featuredMediaId));
      if (featuredSlide) fallbacks.push(featuredSlide);
    }

    if (!fallbacks.length && visibleSlides[0]) {
      fallbacks.push(visibleSlides[0]);
    }

    return fallbacks;
  }

  /**
   * Updates the single card image for the selected shade swatch.
   */
  /**
   * Syncs card images from the selected swatch, but does not strip a liquid-rendered
   * hover pair on first paint when the swatch already matches that primary image.
   */
  #updateShadeGallery() {
    let selectedInput = /** @type {HTMLInputElement | undefined} */ (
      this.querySelector('swatches-variant-picker-component input[type="radio"]:checked') ??
        this.variantPicker?.selectedOption
    );

    if (!selectedInput) {
      const firstInput = this.querySelector('swatches-variant-picker-component input[type="radio"]');
      if (!(firstInput instanceof HTMLInputElement)) return;
      selectedInput = firstInput;
      firstInput.checked = true;
    }

    this.#updateCardImageFromSwatch(selectedInput);
  }

  /**
   * Swaps the product card images from a swatch input's media URLs.
   * @param {HTMLInputElement} selectedInput
   */
  #updateCardImageFromSwatch(selectedInput) {
    const mediaUrl = selectedInput.dataset.optionMediaUrl;
    if (!mediaUrl) return;

    const wrap = this.#getCardMediaWrap();
    const primaryImage = this.#getCardImage();
    const hoverImage = this.#getCardHoverImage();
    if (!primaryImage) return;

    const mediaId = selectedInput.dataset.optionMediaId;
    // Swatch media is served at a single 800w size, so the srcset is derived rather than shipped per swatch.
    const srcset = `${mediaUrl} 800w`;
    const hoverUrl = selectedInput.dataset.optionMediaHoverUrl || '';
    const hoverSrcset = hoverUrl ? `${hoverUrl} 800w` : '';
    const hoverId = selectedInput.dataset.optionMediaHoverId || '';

    const cardMedia = this.refs.cardMedia ?? this.querySelector('[ref="cardMedia"]');
    if (mediaId && cardMedia instanceof HTMLElement) {
      cardMedia.dataset.mediaId = mediaId;
      const mediaWrapper = cardMedia.querySelector('.product-media');
      if (mediaWrapper instanceof HTMLElement) {
        mediaWrapper.dataset.mediaId = mediaId;
      }
    }

    primaryImage.src = mediaUrl;
    if (srcset) {
      primaryImage.srcset = srcset;
    } else {
      primaryImage.removeAttribute('srcset');
    }
    primaryImage.removeAttribute('loading');

    if (wrap instanceof HTMLElement) {
      wrap.dataset.primarySrc = mediaUrl;
      wrap.dataset.primarySrcset = srcset;
    }

    if (wrap instanceof HTMLElement) {
      if (hoverUrl && hoverUrl !== mediaUrl) {
        wrap.dataset.hoverSrc = hoverUrl;
        wrap.dataset.hoverSrcset = hoverSrcset;
        wrap.classList.add('card-gallery__media--has-hover');
        wrap.setAttribute('data-has-hover-image', '');

        // The dataset above is kept current either way, so a resize up to desktop still
        // resolves the right shade on its first hover. Only the fetch is withheld on
        // mobile — assigning src here would download eagerly no matter what the CSS does
        // with the layer.
        if (isDesktopBreakpoint()) {
          let nextHoverImage = hoverImage;
          if (!nextHoverImage) {
            nextHoverImage = this.#createHoverMediaLayer(wrap, hoverUrl, hoverSrcset);
          }

          if (nextHoverImage instanceof HTMLImageElement) {
            nextHoverImage.src = hoverUrl;
            if (hoverSrcset) {
              nextHoverImage.srcset = hoverSrcset;
            } else {
              nextHoverImage.removeAttribute('srcset');
            }
            nextHoverImage.removeAttribute('loading');
          }
        }

        const hoverMedia = this.refs.cardHoverMedia ?? this.querySelector('[ref="cardHoverMedia"]');
        if (hoverId && hoverMedia instanceof HTMLElement) {
          hoverMedia.dataset.mediaId = hoverId;
        }
      } else {
        // No second shade-named image — clear hover so another shade's image is not shown.
        delete wrap.dataset.hoverSrc;
        delete wrap.dataset.hoverSrcset;
        wrap.classList.remove('card-gallery__media--has-hover', 'is-showing-hover');
        wrap.removeAttribute('data-has-hover-image');
        this.classList.remove('is-card-hovering');
      }
    }

    this.#preloadHoverImage();
  }

  /**
   * @deprecated Kept for compatibility; hover URLs are stored on the media wrap.
   * @param {HTMLInputElement} selectedInput
   */
  #updateCardHoverImageFromSwatch(selectedInput) {
    this.#updateCardImageFromSwatch(selectedInput);
  }

  /**
   * Clones the primary media node into an absolute hover layer.
   * @param {HTMLElement} wrap
   * @param {string} hoverSrc
   * @param {string} [hoverSrcset]
   * @returns {HTMLImageElement | null}
   */
  #createHoverMediaLayer(wrap, hoverSrc, hoverSrcset = '') {
    if (!(wrap instanceof HTMLElement) || !hoverSrc) return null;

    const existing = this.#getCardHoverImage();
    if (existing) return existing;

    const primary = this.refs.cardMedia ?? wrap.querySelector('.card-gallery__image--primary');
    if (!(primary instanceof HTMLElement)) return null;

    const hoverMedia = /** @type {HTMLElement} */ (primary.cloneNode(true));
    hoverMedia.classList.remove('card-gallery__image--primary');
    hoverMedia.classList.add('card-gallery__image--hover');
    hoverMedia.setAttribute('ref', 'cardHoverMedia');
    hoverMedia.setAttribute('aria-hidden', 'true');

    const hoverImage = hoverMedia.querySelector('img');
    if (!(hoverImage instanceof HTMLImageElement)) return null;

    hoverImage.src = hoverSrc;
    if (hoverSrcset) {
      hoverImage.srcset = hoverSrcset;
    } else {
      hoverImage.removeAttribute('srcset');
    }
    hoverImage.removeAttribute('loading');
    hoverImage.removeAttribute('ref');

    wrap.appendChild(hoverMedia);
    return hoverImage;
  }

  /**
   * Updates dot pagination and arrows to match visible slides.
   * @param {number} visibleCount
   */
  #updateGalleryControls(visibleCount) {
    const hasShadeGallery = Boolean(this.querySelector('[data-shade-gallery="true"]'));

    // Product cards show a single image only — never reveal slider controls.
    const controls = this.querySelector('slideshow-controls');
    if (controls instanceof HTMLElement) {
      controls.hidden = true;
    }

    const arrows = this.querySelector('.card-gallery slideshow-arrows.product-card-gallery__arrows');
    if (arrows instanceof HTMLElement) {
      arrows.hidden = true;
    }

    if (hasShadeGallery) {
      return;
    }

    this.#updateGalleryDotCount(1);
  }

  /**
   * Updates dot pagination to match the number of visible slides.
   * @param {number} visibleCount
   */
  #updateGalleryDotCount(visibleCount) {
    const dotItems = this.querySelectorAll('.slideshow-controls__dots li');

    dotItems.forEach((item, index) => {
      if (index < visibleCount) {
        item.removeAttribute('hidden');
      } else {
        item.setAttribute('hidden', '');
      }
    });

    const dotsList = this.querySelector('.slideshow-controls__dots');
    if (dotsList instanceof HTMLElement) {
      dotsList.hidden = true;
    }

    const controls = this.querySelector('slideshow-controls');
    if (controls instanceof HTMLElement) {
      controls.hidden = true;
    }
  }

  /**
   * Legacy single-image variant preview for products without shade-tagged slides.
   */
  #updateVariantImagesLegacy() {
    const selectedOption = this.variantPicker?.selectedOption;
    if (!(selectedOption instanceof HTMLInputElement)) return;
    this.#updateCardImageFromSwatch(selectedOption);
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const matchingInput = this.querySelector(`input[data-option-media-id="${id}"]`);
    if (matchingInput instanceof HTMLInputElement) {
      this.resetVariant.cancel();
      this.#updateCardImageFromSwatch(matchingInput);
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const selectedOption = this.variantPicker?.selectedOption;
    if (selectedOption instanceof HTMLInputElement) {
      this.#updateCardImageFromSwatch(selectedOption);
      return;
    }

    this.#updateShadeGallery();
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest(
      'button, input, label, select, [tabindex="1"], .slideshow-control, slideshow-arrows, slideshow-controls'
    );

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Arrow controls for product card swatch slider.
   */
  #bindSwatchSliderArrows() {
    const wrap = this.querySelector('.product-card-swatches-slider-wrap');
    const slider = wrap?.querySelector('.product-card-swatches-slider');

    if (!(wrap instanceof HTMLElement) || !(slider instanceof HTMLElement)) {
      return;
    }

    const isRtl = () =>
      getComputedStyle(slider).direction === 'rtl' ||
      document.documentElement.classList.contains('is-arabic') ||
      (document.documentElement.getAttribute('lang') || '').toLowerCase().startsWith('ar');

    const getScrollEdges = (rtl) => {
      const maxScroll = Math.max(0, slider.scrollWidth - slider.clientWidth);
      const scrollLeft = slider.scrollLeft;
      const epsilon = 2;

      if (!rtl) {
        return {
          maxScroll,
          atStart: scrollLeft <= epsilon,
          atEnd: scrollLeft >= maxScroll - epsilon,
        };
      }

      // Chrome/Safari RTL: 0 at start, negative toward end.
      // Firefox RTL: maxScroll at start, 0 at end.
      if (scrollLeft <= 0) {
        return {
          maxScroll,
          atStart: scrollLeft >= -epsilon,
          atEnd: scrollLeft <= -maxScroll + epsilon,
        };
      }

      return {
        maxScroll,
        atStart: scrollLeft >= maxScroll - epsilon,
        atEnd: scrollLeft <= epsilon,
      };
    };

    const updateArrows = () => {
      const prev = wrap.querySelector('[data-swatch-slider-prev]');
      const next = wrap.querySelector('[data-swatch-slider-next]');

      if (!(prev instanceof HTMLButtonElement) || !(next instanceof HTMLButtonElement)) return;

      const swatchCount =
        parseInt(slider.dataset.swatchCount || '0', 10) ||
        slider.querySelectorAll('.variant-option__swatch').length;
      const hasManySwatches = wrap.dataset.manySwatches === 'true' || swatchCount > 7;
      const rtl = isRtl();
      const { maxScroll, atStart: scrolledToStart, atEnd: scrolledToEnd } = getScrollEdges(rtl);
      const hasOverflow = maxScroll > 2;

      let atStart = scrolledToStart;
      let atEnd = scrolledToEnd;

      // LTR only: refine with geometry. RTL geometry was previously incorrect and hid both arrows.
      if (!rtl) {
        const firstSwatch = slider.querySelector('.variant-option__swatch:first-child');
        const lastSwatch = slider.querySelector('.variant-option__swatch:last-child');
        const sliderRect = slider.getBoundingClientRect();

        if (firstSwatch instanceof HTMLElement) {
          atStart = firstSwatch.getBoundingClientRect().left >= sliderRect.left - 2;
        }

        if (lastSwatch instanceof HTMLElement) {
          atEnd = lastSwatch.getBoundingClientRect().right <= sliderRect.right + 2;
        }
      }

      if (!hasOverflow && !hasManySwatches) {
        atStart = true;
        atEnd = true;
      }

      wrap.classList.toggle('product-card-swatches-slider-wrap--active', hasManySwatches || hasOverflow);
      wrap.classList.toggle('product-card-swatches-slider-wrap--at-start', atStart);
      wrap.classList.toggle('product-card-swatches-slider-wrap--at-end', atEnd);
      wrap.classList.toggle('product-card-swatches-slider-wrap--rtl', rtl);
    };

    const slide = (direction) => {
      const firstSwatch = slider.querySelector('.variant-option__swatch');
      const swatchWidth = firstSwatch instanceof HTMLElement ? firstSwatch.offsetWidth + 12 : 44;
      const visibleCount = Math.max(1, Math.floor(slider.clientWidth / swatchWidth));
      const amount = swatchWidth * Math.min(visibleCount, 3);
      // RTL engines invert scrollLeft — move toward end with the opposite delta.
      const distance = amount * direction * (isRtl() ? -1 : 1);

      slider.scrollBy({ left: distance, behavior: 'smooth' });

      const refreshAfterScroll = () => {
        updateArrows();
        setTimeout(updateArrows, 350);
      };

      if ('onscrollend' in slider) {
        slider.addEventListener('scrollend', refreshAfterScroll, { once: true });
      } else {
        refreshAfterScroll();
      }
    };

    if (wrap.dataset.swatchSliderBound !== 'true') {
      wrap.dataset.swatchSliderBound = 'true';

      wrap.addEventListener('click', (event) => {
        if (!(event.target instanceof Element)) return;

        const prevButton = event.target.closest('[data-swatch-slider-prev]');
        const nextButton = event.target.closest('[data-swatch-slider-next]');

        if (!prevButton && !nextButton) return;

        event.preventDefault();
        event.stopPropagation();
        slide(prevButton ? -1 : 1);
      });

      slider.addEventListener('scroll', updateArrows, { passive: true });
      slider.addEventListener('scrollend', updateArrows);
      const viewport = wrap.querySelector('.product-card-swatches-slider-viewport');
      new ResizeObserver(updateArrows).observe(slider);
      if (viewport instanceof HTMLElement) {
        new ResizeObserver(updateArrows).observe(viewport);
      }
      new ResizeObserver(updateArrows).observe(wrap);
    }

    slider.querySelectorAll('img').forEach((image) => {
      if (!(image instanceof HTMLImageElement)) return;
      if (image.complete) return;
      image.addEventListener('load', updateArrows, { once: true });
    });

    updateArrows();
    requestAnimationFrame(() => {
      updateArrows();
      setTimeout(updateArrows, 150);
    });
  }

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.variantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}